import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPaperclip, faTimes, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import AnimatedPlaceholder from '../AnimatedPlaceholder/AnimatedPlaceholder';
import './AiPromptComposer.css';

const getPromptsClassName = (used, max) => {
  if (!max || max <= 0) return 'credits-limit';
  const usage = (used / max) * 100;
  if (usage < 50) return 'usage-low';
  if (usage < 90) return 'usage-medium';
  return 'usage-high';
};

const formatNextPromptRenewal = (isoString) => {
  if (!isoString) return null;
  const dt = new Date(isoString);
  return dt.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const AiPromptComposer = ({
  value,
  onChange,
  onKeyDown,
  onSubmit,
  textareaRef,
  isProcessing = false,
  canSubmit = false,
  isRefinement = false,
  sendButtonAriaLabel = 'Generate Strategy',
  sendButtonTestId = 'ai-prompt-send-button',
  className = '',
  attachmentsConfig = null,
  creditsConfig = null,
  textareaProps = {},
  placeholderExamples = null,
}) => {
  const {
    enabled: attachmentsEnabled = false,
    inputRef: attachmentInputRef = null,
    onAttachmentsSelected = null,
    accept = '.py,.js,.ts,.jsx,.tsx,.java,.cpp,.c,.h,.go,.rs,.rb,.php,.csv,.xlsx,.xls,.json,.xml,.txt,.md,.pdf',
    attachments = [],
    onRemoveAttachment = () => {},
    attachmentButtonDisabled = false,
    attachmentButtonLabel = 'Attach files',
    attachmentButtonAriaLabel = 'Attach files',
  } = attachmentsConfig || {};

  const {
    used = 0,
    max = 0,
    nextRenewalDate = null,
    show = false,
    tooltipPlacement = 'top',
    labelFormatter,
  } = creditsConfig || {};

  const creditsLabel = useMemo(() => {
    if (typeof labelFormatter === 'function') {
      return labelFormatter({ used, max, nextRenewalDate });
    }
    return `${used}/${max} prompts`;
  }, [labelFormatter, max, nextRenewalDate, used]);

  const handleAttachmentButtonClick = () => {
    if (!attachmentsEnabled) return;
    if (attachmentsConfig?.onAttachmentButtonClick) {
      attachmentsConfig.onAttachmentButtonClick();
      return;
    }
    if (attachmentInputRef && attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
  };

  const containerClass = `ai-prompt-composer ${className}`.trim();

  return (
    <div className={containerClass}>
      <div className="aibot-input-section">
        <div className="aibot-chat-input-wrapper">
          <AnimatedPlaceholder isVisible={!value} isRefinementMode={isRefinement} customExamples={placeholderExamples} />
          <textarea
            ref={textareaRef}
            className="aibot-chat-input"
            value={value}
            onChange={(event) => onChange?.(event.target.value, event)}
            onKeyDown={onKeyDown}
            disabled={isProcessing || textareaProps.disabled}
            readOnly={isProcessing || textareaProps.readOnly}
            rows={textareaProps.rows || 4}
            data-testid="ai-prompt-textarea"
            data-hj-allow
            {...textareaProps}
          />
        </div>

        <div className="aibot-input-footer">
          <div className="aibot-footer-left">
            {attachmentsEnabled && (
              <div className="aibot-attachment-section">
                <input
                  ref={attachmentInputRef}
                  type="file"
                  multiple
                  accept={accept}
                  onChange={onAttachmentsSelected}
                  style={{ display: 'none' }}
                  data-testid="ai-prompt-attachments-input"
                />

                <button
                  type="button"
                  className="aibot-attachment-button"
                  onClick={handleAttachmentButtonClick}
                  disabled={attachmentButtonDisabled || isProcessing}
                  aria-label={attachmentButtonAriaLabel}
                  data-testid="ai-prompt-attachment-button"
                >
                  <FontAwesomeIcon icon={faPaperclip} className="me-1" />
                  <span className="d-none d-md-inline">{attachmentButtonLabel}</span>
                </button>

                {attachments?.length > 0 && (
                  <div className="aibot-attached-files">
                    {attachments.map((file) => (
                      <div key={file.id || file.name} className="aibot-attached-file-chip">
                        <span className="file-name">{file.name}</span>
                        <button
                          type="button"
                          className="remove-file"
                          onClick={() => onRemoveAttachment(file.id || file.name)}
                          disabled={isProcessing}
                          aria-label={`Remove ${file.name}`}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {show && (
              <div className="aibot-credits-info">
                <OverlayTrigger
                  placement={tooltipPlacement}
                  overlay={
                    <Tooltip id="credits-tooltip">
                      <div className="aibot-credits-tooltip-content">
                        {`You've used ${used} out of ${max} AI prompts this month.`}
                        {nextRenewalDate && (
                          <div className="aibot-next-credit-info">
                            {`Renews on ${formatNextPromptRenewal(nextRenewalDate)}`}
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  }
                >
                  <div className={`aibot-credits-counter ${getPromptsClassName(used, max)}`}>
                    <span>{creditsLabel}</span>
                    <FontAwesomeIcon icon={faExclamationCircle} className="aibot-info-icon" />
                  </div>
                </OverlayTrigger>
              </div>
            )}
          </div>

          <div className="aibot-footer-right">
            <div className="aibot-send-button-container">
              <button
                type="button"
                className="aibot-send-button generate-button"
                onClick={onSubmit}
                disabled={!canSubmit || isProcessing}
                aria-label={sendButtonAriaLabel}
                data-testid={sendButtonTestId}
              >
                {isProcessing ? (
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} className="aibot-send-button-icon" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AiPromptComposer.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  textareaRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  isProcessing: PropTypes.bool,
  canSubmit: PropTypes.bool,
  isRefinement: PropTypes.bool,
  sendButtonAriaLabel: PropTypes.string,
  sendButtonTestId: PropTypes.string,
  className: PropTypes.string,
  attachmentsConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    onAttachmentsSelected: PropTypes.func,
    accept: PropTypes.string,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string.isRequired,
      })
    ),
    onRemoveAttachment: PropTypes.func,
    attachmentButtonDisabled: PropTypes.bool,
    attachmentButtonLabel: PropTypes.string,
    attachmentButtonAriaLabel: PropTypes.string,
    onAttachmentButtonClick: PropTypes.func,
  }),
  creditsConfig: PropTypes.shape({
    used: PropTypes.number,
    max: PropTypes.number,
    nextRenewalDate: PropTypes.string,
    show: PropTypes.bool,
    tooltipPlacement: PropTypes.string,
    labelFormatter: PropTypes.func,
  }),
  textareaProps: PropTypes.object,
  placeholderExamples: PropTypes.arrayOf(PropTypes.string),
};

export default AiPromptComposer;
